import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { LoadingModel } from '../../_core/state/loading.model';
import { GamificationService } from '../services/gamification.service';
import { GetBadges, GetGeneralScore, GetMonthlyScore, GetRanking, SetLoadingState } from './gamification.actions';
import dayjs from 'dayjs';
import { IRanking } from '../services/models/ranking.model';
import { CategoryBadges } from '../services/models/badges.model';
import { patch } from '@ngxs/store/operators';
import { ScoreDetail } from '../services/models/score-detail.model';

export type GamificationStateModel = LoadingModel & {
  ranking: IRanking;
  badges: CategoryBadges[];
  monthlyScore: ScoreDetail;
  generalScore: ScoreDetail;
};

export const initialState: GamificationStateModel = {
  ranking: null,
  badges: [],
  monthlyScore: {
    actions: [],
  },
  generalScore: {
    actions: [],
  },
  isLoading: false,
};

@State<GamificationStateModel>({
  name: 'gamification',
  defaults: initialState,
})
@Injectable()
export class GamificationState {
  private readonly gamificationService = inject(GamificationService);

  @Selector()
  static getIsLoading(state: GamificationStateModel) {
    return state.isLoading;
  }
  @Selector()
  static getMonthlyRanking(state: GamificationStateModel) {
    return state.ranking.monthlyData;
  }
  @Selector()
  static getGeneralRanking(state: GamificationStateModel) {
    return state.ranking.generalData;
  }
  @Selector()
  static getBadges(state: GamificationStateModel) {
    return state.badges;
  }
  @Selector()
  static getMonthlyScore(state: GamificationStateModel) {
    return state.monthlyScore;
  }
  @Selector()
  static getGeneralScore(state: GamificationStateModel) {
    return state.generalScore;
  }

  @Action(GetRanking)
  getRanking(context: StateContext<GamificationStateModel>): Observable<IRanking> {
    return this.gamificationService.getRanking().pipe(
      tap((ranking) => {
        context.setState(patch<GamificationStateModel>({ ranking }));
      })
    );
  }

  @Action(GetBadges)
  getBadges(context: StateContext<GamificationStateModel>): Observable<CategoryBadges[]> {
    return this.gamificationService.getBadges().pipe(
      tap((badges) => {
        context.setState(patch<GamificationStateModel>({ badges }));
      })
    );
  }

  @Action(GetMonthlyScore)
  getMonthlyScore(context: StateContext<GamificationStateModel>): Observable<ScoreDetail> {
    const date = `${dayjs().year()}-${dayjs().format('MM')}`;

    context.setState(
      patch<GamificationStateModel>({ monthlyScore: initialState.monthlyScore })
    );

    return this.gamificationService.getScores(date).pipe(
      tap((monthlyScore) => {
        context.setState(patch<GamificationStateModel>({ monthlyScore }));
      })
    );
  }

  @Action(GetGeneralScore)
  getGeneralScore(context: StateContext<GamificationStateModel>): Observable<ScoreDetail> {

    context.setState(
      patch<GamificationStateModel>({
        generalScore: {
          ...initialState.generalScore
        },
      })
    );

    context.setState(
      patch<GamificationStateModel>({ generalScore: initialState.generalScore })
    );

    return this.gamificationService.getScores().pipe(
      tap((generalScore) => {
        context.setState(patch<GamificationStateModel>({ generalScore }));
      })
    );
  }

  @Action(SetLoadingState)
  setLoadingState(context: StateContext<GamificationStateModel>, { isLoading }: SetLoadingState) {
    return context.setState(patch<GamificationStateModel>({ isLoading: isLoading }));
  }
}
